// Consuming GPT-Live input transcript segments exactly once.
//
// Segments: {delta, start_ms, end_ms} on the session timeline, half-open
// [start_ms, end_ms). Observed in a real Live ASR trace (2026-09-13): the
// delegation's offset_ms equals the START of the last spoken segment
// (" cards" 44000–44200, offset 44000). A naive "start < offset" rule drops
// that last word and leaks it into the next turn. So:
//
//   effective watermark W = max(end_ms) over observed, placeable segments with
//                           start_ms <= offset_ms (the frame containing the
//                           boundary), or offset_ms if none extends past it;
//   consume segments with start_ms in [previous, W); advance previous := W.
//
// The boundary segment is consumed exactly once and never reused. Rules:
//   - invalid offset (missing, non-finite, negative, or < previous) consumes
//     NOTHING and moves nothing; there is no replay path. Offset 0 is valid:
//     a first utterance [0, 200) delegated at 0 is consumed;
//   - an EMPTY window never advances the watermark: if no placed text falls
//     in [previous, W) nothing is consumed and `previous` stays put, so a
//     transcript that arrives late, an explicit Send, or a Live retry can
//     still answer that speech exactly once;
//   - segments without a finite start_ms are never consumed by an offset;
//   - exact duplicates are dropped at insert; a same-start revision replaces
//     the unconsumed original, or is recorded-as-consumed if already answered;
//   - a late fragment from before the boundary that extends past it is kept for
//     the next window; one fully inside an answered window is retired.

function finite(n) { return typeof n === 'number' && Number.isFinite(n); }

export function addSegment(segments, seg) {
  if (!seg) return segments;
  const delta = seg.delta ?? '';
  const start_ms = finite(seg.start_ms) ? seg.start_ms : null;
  const end_ms = finite(seg.end_ms) ? seg.end_ms : null;
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    if (s.start_ms === start_ms && s.end_ms === end_ms && s.delta === delta) return segments;
    if (start_ms !== null && s.start_ms === start_ms) { segments[i] = { delta, start_ms, end_ms, consumed: !!s.consumed }; return segments; }
  }
  segments.push({ delta, start_ms, end_ms, consumed: false });
  return segments;
}

// The exclusive end of the window for a delegation at offsetMs.
export function effectiveWatermark(segments, offsetMs) {
  let w = offsetMs;
  for (const s of segments || []) {
    if (!s || s.consumed || !finite(s.start_ms)) continue;
    if (s.start_ms <= offsetMs && finite(s.end_ms) && s.end_ms > w) w = s.end_ms;
  }
  return w;
}

export function consumeWindow(segments, consumedUntil, offsetMs) {
  const from = finite(consumedUntil) && consumedUntil >= 0 ? consumedUntil : 0;
  const valid = finite(offsetMs) && offsetMs >= 0 && offsetMs >= from;
  if (!valid) return { text: '', consumedUntil: from, count: 0, invalidOffset: true, empty: true, watermark: from };
  const watermark = effectiveWatermark(segments, offsetMs);
  const picked = [];
  for (const s of segments || []) {
    if (!s || s.consumed || !finite(s.start_ms)) continue;
    const st = s.start_ms;
    if (st >= from && st < watermark) { picked.push(s); continue; }
    if (st < from) {
      if (finite(s.end_ms) && s.end_ms > from) picked.push(s); // straddling late fragment: keep its tail
      else s.consumed = true;                                   // belongs to an answered window
    }
  }
  picked.sort((a, b) => a.start_ms - b.start_ms);
  const text = picked.map(s => s.delta).join('').replace(/\s+/g, ' ').trim();
  // Empty watermark: consume nothing, move nothing.
  if (!text) return { text: '', consumedUntil: from, count: 0, invalidOffset: false, empty: true, watermark: from };
  for (const s of picked) s.consumed = true;
  return { text, consumedUntil: watermark, count: picked.length, invalidOffset: false, empty: false, watermark, start_ms: picked[0].start_ms, end_ms: watermark };
}

// Consume every placed, unconsumed segment at or after `consumedUntil`
// (explicit Send, or a delegation whose offset pointed at silence). The
// watermark is the END of the last such word, never an unbounded value, so
// later speech is still "new". Empty → nothing consumed, nothing moved.
export function consumeAll(segments, consumedUntil) {
  const from = finite(consumedUntil) && consumedUntil >= 0 ? consumedUntil : 0;
  const picked = (segments || []).filter(s => s && !s.consumed && finite(s.start_ms) && s.start_ms >= from).sort((a, b) => a.start_ms - b.start_ms);
  const text = picked.map(s => s.delta).join('').replace(/\s+/g, ' ').trim();
  if (!text) return { text: '', consumedUntil: from, count: 0, invalidOffset: false, empty: true, watermark: from };
  const watermark = picked.reduce((w, s) => Math.max(w, finite(s.end_ms) ? s.end_ms : s.start_ms), from);
  for (const s of picked) s.consumed = true;
  return { text, consumedUntil: watermark, count: picked.length, invalidOffset: false, empty: false, watermark, start_ms: picked[0].start_ms, end_ms: watermark };
}

// Retire speech that Live answered in its own voice. Takes every unconsumed
// placed segment that started before `boundaryMs` (the start of Live's reply)
// and then any LATE TAIL of that same utterance: segments contiguous on the
// ASR timeline (start within gapMs of the retired end), since ASR can deliver
// the last word after Live has already begun answering. Speech that starts
// after a real pause is a new utterance (a barge-in) and stays pending.
export function retireAnswered(segments, consumedUntil, boundaryMs, gapMs = 400) {
  const from = finite(consumedUntil) && consumedUntil >= 0 ? consumedUntil : 0;
  if (!finite(boundaryMs)) return { text: '', consumedUntil: from, count: 0, empty: true, watermark: from };
  const open = (segments || []).filter(s => s && !s.consumed && finite(s.start_ms) && s.start_ms >= from).sort((a, b) => a.start_ms - b.start_ms);
  const picked = [];
  let end = null;
  for (const s of open) {
    const segEnd = finite(s.end_ms) ? s.end_ms : s.start_ms;
    const before = s.start_ms < boundaryMs;
    const tail = end !== null && s.start_ms <= end + gapMs;
    if (!before && !tail) break;
    picked.push(s); end = Math.max(end ?? segEnd, segEnd);
  }
  const text = picked.map(s => s.delta).join('').replace(/\s+/g, ' ').trim();
  if (!text) return { text: '', consumedUntil: from, count: 0, empty: true, watermark: from };
  for (const s of picked) s.consumed = true;
  return { text, consumedUntil: end, count: picked.length, empty: false, watermark: end };
}

// Whether a placed, unconsumed segment with real words exists at or after
// `consumedUntil`: the transcript has speech that no delegation answered yet.
export function hasPendingSpeech(segments, consumedUntil) {
  return /[\p{L}\p{N}]/u.test(pendingText(segments, consumedUntil));
}

// Classify an incoming input delta before it is added. "New speech" means the
// user said something not yet answered: a segment that is NOT a revision of an
// existing start, carries at least one letter or digit, and starts at or after
// the consumed watermark. Revisions of an utterance already heard, empty or
// punctuation-only frames, unplaced frames and late fragments of an answered
// window are bookkeeping, not new input: they must not count as barge-in or
// invalidate a pending answer.
export function classifyInput(segments, consumedUntil, seg) {
  const start = finite(seg?.start_ms) ? seg.start_ms : null;
  const words = /[\p{L}\p{N}]/u.test(String(seg?.delta ?? ''));
  const from = finite(consumedUntil) ? consumedUntil : 0;
  if (start === null) return 'unplaced';
  if ((segments || []).some(s => s && s.start_ms === start)) return 'revision';
  if (!words) return 'empty';
  if (start < from) return 'late';
  return 'new';
}

// Group output (spoken) transcript segments into utterances: a new utterance
// starts after a silence of at least gapMs on the session timeline. Returns
// [{ id, start_ms, end_ms, text }] in order; id is the utterance's start.
export function utterances(segments, gapMs = 1200) {
  const placed = (segments || []).filter(s => s && finite(s.start_ms)).sort((a, b) => a.start_ms - b.start_ms);
  const groups = [];
  for (const s of placed) {
    const last = groups[groups.length - 1];
    if (last && s.start_ms - last.end_ms <= gapMs) { last.parts.push(s); last.end_ms = Math.max(last.end_ms, finite(s.end_ms) ? s.end_ms : s.start_ms); }
    else groups.push({ id: s.start_ms, start_ms: s.start_ms, end_ms: finite(s.end_ms) ? s.end_ms : s.start_ms, parts: [s] });
  }
  return groups.map(g => ({ id: g.id, start_ms: g.start_ms, end_ms: g.end_ms, text: g.parts.map(x => x.delta ?? '').join('').replace(/\s+/g, ' ').trim() }));
}

// Text not yet consumed (the utterance in progress), for the transient caption.
export function pendingText(segments, consumedUntil) {
  const from = finite(consumedUntil) ? consumedUntil : 0;
  return (segments || []).filter(s => s && !s.consumed && finite(s.start_ms) && s.start_ms >= from).sort((a, b) => a.start_ms - b.start_ms).map(s => s.delta ?? '').join('').replace(/\s+/g, ' ').trim();
}

export function recentText(segments, maxChars = 1200) {
  const placed = (segments || []).filter(s => s && finite(s.start_ms)).sort((a, b) => a.start_ms - b.start_ms);
  const loose = (segments || []).filter(s => s && !finite(s.start_ms));
  const s = [...placed, ...loose].map(x => x.delta ?? '').join('').replace(/\s+/g, ' ').trim();
  return s.length > maxChars ? s.slice(-maxChars) : s;
}
